import { type Store } from '../../../../services/stores/stores-sdk'

// Builds store-specific image URL by replacing image name with store ID
export const buildStoreImageUrl = (
  storeId: string,
  defaultImageUrl: string
): string => {
  if (!storeId || !defaultImageUrl) {
    return defaultImageUrl
  }

  const lastSlashIndex = defaultImageUrl.lastIndexOf('/')
  const questionMarkIndex = defaultImageUrl.indexOf('?')

  if (lastSlashIndex === -1) {
    return defaultImageUrl
  }

  const baseUrl = defaultImageUrl.substring(0, lastSlashIndex + 1)
  const params =
    questionMarkIndex !== -1 ? defaultImageUrl.substring(questionMarkIndex) : ''

  return `${baseUrl}${storeId}${params}`
}

// Validates store image via HEAD request, returns store URL or default
export const validateStoreImage = async (
  store: Store,
  defaultImageUrl: string
): Promise<string> => {
  if (!store.storeId || !defaultImageUrl) {
    return defaultImageUrl
  }

  const storeImageUrl = buildStoreImageUrl(store.storeId, defaultImageUrl)

  try {
    const response = await fetch(storeImageUrl, {
      method: 'HEAD',
      redirect: 'manual'
    })

    return response.ok ? storeImageUrl : defaultImageUrl
  } catch (error) {
    return defaultImageUrl
  }
}

// Validates multiple store images in parallel
export const validateStoreImages = async (
  stores: Store[],
  defaultImageUrl: string
): Promise<Map<string, string>> => {
  const validatedImages = new Map<string, string>()

  if (!defaultImageUrl || !stores.length) {
    return validatedImages
  }

  const validationPromises = stores.map(async (store) => {
    if (store.storeId) {
      const validatedUrl = await validateStoreImage(store, defaultImageUrl)
      return { storeId: store.storeId, url: validatedUrl }
    }
    return null
  })

  const results = await Promise.allSettled(validationPromises)

  results.forEach((result) => {
    if (result.status === 'fulfilled' && result?.value?.storeId) {
      validatedImages.set(result.value.storeId, result.value.url)
    }
  })

  return validatedImages
}
